import { useTranslation } from 'react-i18next';
import { useFamilyScope } from '@/contexts/FamilyScopeContext';
import { ChevronDown, User, Users, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export function ScopeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation('nav');
  const ROLE_LABEL: Record<string, string> = {
    owner: t('scope.role.owner'),
    co_manager: t('scope.role.coManager'),
    contributor: t('scope.role.contributor'),
    scanner: t('scope.role.scanner'),
    viewer: t('scope.role.viewer'),
  };
  const { scope, setScope, availableFamilies } = useFamilyScope();
  const navigate = useNavigate();

  // If user belongs to no other family, hide the switcher entirely
  if (availableFamilies.length === 0) return null;

  const active = scope.type === 'family'
    ? availableFamilies.find((f) => f.groupId === scope.groupId)
    : null;

  const label = scope.type === 'personal' ? t('scope.myAccount') : scope.groupName;
  const Icon = scope.type === 'personal' ? User : Users;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border/30 bg-card/40 hover:bg-card/70 text-[11px] font-medium text-foreground transition-colors ${className || ''}`}>
        <Icon className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{label}</span>
        {active?.role && (
          <span className="text-[9px] px-1 py-px rounded bg-primary/10 text-primary">
            {ROLE_LABEL[active.role] || active.role}
          </span>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('scope.viewAs')}</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => setScope({ type: 'personal' })}
          className="text-xs"
        >
          <User className="h-3.5 w-3.5 me-2" />
          {t('scope.myAccount')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {availableFamilies.map((f) => (
          <DropdownMenuItem
            key={f.groupId}
            onClick={() => setScope({ type: 'family', ownerId: f.ownerId, groupId: f.groupId, groupName: f.groupName })}
            className="text-xs"
          >
            <Users className="h-3.5 w-3.5 me-2" />
            <span className="truncate flex-1">{f.groupName}</span>
            {f.role && (
              <span className="text-[9px] text-muted-foreground/70 ms-2 shrink-0">
                {ROLE_LABEL[f.role] || f.role}
              </span>
            )}
          </DropdownMenuItem>
        ))}
        {active?.role === 'scanner' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/scanner')} className="text-xs">
              <Camera className="h-3.5 w-3.5 me-2" />
              {t('scope.scanForThisFamily')}
            </DropdownMenuItem>
          </>
        )}
        <div className="px-2 py-1.5 text-[10px] text-muted-foreground/70 leading-relaxed">
          {t('scope.footerNote')}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
